import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 轻量数据加载 hook。
 * - 请求失败时保留 fallback（一般为 mock 数据）作为兜底渲染，保证页面可用；
 * - deps 变化时自动重新请求；refresh 手动刷新。
 */

export interface UseRequestOptions<T> {
  /** 失败/加载中时的兜底数据（一般传 mock 数据） */
  fallback?: T;
  /** 变化时重新请求的依赖（如筛选条件） */
  deps?: unknown[];
}

export interface UseRequestResult<T> {
  data: T;
  loading: boolean;
  /** 请求失败信息（存在时页面以 fallback 数据渲染） */
  error: string | null;
  refresh: () => void;
}

export function useRequest<T>(fetcher: () => Promise<T>, options: UseRequestOptions<T> = {}): UseRequestResult<T> {
  const { fallback, deps = [] } = options;

  const [data, setData] = useState<T>(fallback as T);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 保留 fallback 数据，仅记录错误
        setError(err instanceof Error ? err.message : '加载失败，请稍后重试');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick, depsKey]);

  const refresh = useCallback(() => {
    setTick((value) => value + 1);
  }, []);

  return { data, loading, error, refresh };
}
